/**
 * WS4 Session Report modal — long-form report surface with sections,
 * callouts, and tables. Renders four legacy tabs (Executive Summary,
 * Outcomes, Risks & Issues, Next Steps), one pane at a time, matching
 * the legacy `#reportModal` tab structure at
 * `_legacy/ws4-assetlocker.js` lines 810-813.
 *
 * Content lives in `./report-content.ts` as plain data, already
 * organised by tab via the `executiveSummary` / `outcomes` / `risks`
 * / `nextSteps` keys — no per-section discriminator needed; the modal
 * simply switches on the active tab and renders the corresponding
 * structured branch.
 *
 * Visual chrome matches `AnalysisModal` and the panel-style modals in
 * `src/components/shell/*` (outline + true-blue glow + Radix dialog
 * via `PanelDialogContent`). The bespoke `TABLE_STYLE` / `TH_STYLE` /
 * `TD_STYLE` / `SectionHeading` / `KpiTile` / `CalloutBlock` /
 * Share Tech Mono mono stack (now routed through `var(--font-mono)`
 * — see `src/index.css`) is intentionally preserved — only
 * the tab bar (`.dt-tab-bar` / `.dt-tab`) and pane wrapper
 * (`.dt-pane.dt-pane-active`) use legacy classes.
 */

import { useEffect, useMemo, useState, type ReactNode, type CSSProperties } from "react";
import { FileText, X } from "lucide-react";
import { useEffectiveWorkshopOverride } from "@/stores/useEffectiveWorkshopOverride";
import { useGeneratedReport } from "./use-generated-report";
import {
  resolveSessionDate,
  withSessionDate,
} from "@/features/workshop/session-date";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";
import type { Workshop } from "@/content/workshops";
import { useDecisions, type Decision } from "@/stores/useDecisions";
import { useFindings, type Finding } from "@/stores/useFindings";
import { useActions, type ActionItem } from "@/stores/useActions";
import {
  getReport,
  type AccentToken,
  type ActionRow,
  type Badge,
  type Callout,
  type DecisionRow,
  type Kpi,
  type MetaItem,
  type OutputRow,
  type ReadinessRow,
  type ReportContent,
  type RiskRow,
} from "./report-content";

const EMPTY_DECISIONS: readonly Decision[] = Object.freeze([]);
const EMPTY_FINDINGS: readonly Finding[] = Object.freeze([]);
const EMPTY_ACTIONS: readonly ActionItem[] = Object.freeze([]);

/** Finding severity → the report's accent vocabulary (for the risk table). */
function severityToAccent(severity: Finding["severity"]): AccentToken {
  switch (severity) {
    case "high":
      return "danger";
    case "medium":
      return "gold";
    case "low":
      return "sky";
    default:
      return "muted";
  }
}

/** Finding status → a badge accent (open → noted → actioned). */
function findingStatusAccent(status: Finding["status"]): AccentToken {
  switch (status) {
    case "actioned":
      return "green";
    case "noted":
      return "sky";
    default:
      return "gold";
  }
}

type ReportModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workshopId: string;
};

type TabId = "executive" | "outcomes" | "risks" | "next";

const TABS: { id: TabId; label: string }[] = [
  { id: "executive", label: "Executive Summary" },
  { id: "outcomes", label: "Outcomes" },
  { id: "risks", label: "Risks & Issues" },
  { id: "next", label: "Next Steps" },
];

const EM_DASH = "—";

/* ----------------------- Accent → CSS var map -------------------- */

function tone(accent: AccentToken | undefined): string {
  switch (accent) {
    case "true-blue":
      return "var(--accent-true-blue)";
    case "sky":
      return "var(--accent-sky)";
    case "gold":
      return "var(--accent-gold)";
    case "green":
      return "var(--accent-green)";
    case "melon":
      return "var(--accent-melon)";
    case "danger":
      return "var(--accent-danger)";
    case "muted":
      return "var(--text-muted)";
    default:
      return "var(--text-secondary)";
  }
}

function toneRgb(accent: AccentToken): string {
  switch (accent) {
    case "true-blue":
      return "var(--accent-true-blue-rgb)";
    case "sky":
      return "var(--accent-sky-rgb)";
    case "gold":
      return "var(--accent-gold-rgb)";
    case "green":
      return "var(--accent-green-rgb)";
    case "melon":
      return "var(--accent-melon-rgb)";
    case "danger":
      return "var(--accent-danger-rgb)";
    case "muted":
      return "120, 146, 169";
  }
}

/* --------------------------- Atoms ------------------------------- */

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontFamily: "'Inter', sans-serif",
        fontSize: "var(--fs-label)",
        fontWeight: 700,
        color: "var(--text-primary)",
        letterSpacing: 1,
        marginBottom: 10,
        paddingBottom: 6,
        borderBottom: "1px solid rgba(var(--accent-true-blue-rgb), 0.15)",
      }}
    >
      {children}
    </div>
  );
}

function Body({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontSize: "var(--fs-micro)",
        color: "var(--text-secondary)",
        lineHeight: 1.75,
      }}
    >
      {children}
    </div>
  );
}

function Section({ children }: { children: ReactNode }) {
  return <div style={{ marginBottom: 22 }}>{children}</div>;
}

function Divider() {
  return (
    <div
      style={{
        height: 1,
        background: "rgba(var(--accent-true-blue-rgb), 0.1)",
        margin: "18px 0",
      }}
    />
  );
}

function BadgePill({ badge }: { badge: Badge }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "1px 7px",
        borderRadius: 10,
        fontSize: "var(--fs-micro)",
        fontWeight: 600,
        letterSpacing: 1,
        color: tone(badge.accent),
        background: `rgba(${toneRgb(badge.accent)}, 0.1)`,
        border: `1px solid rgba(${toneRgb(badge.accent)}, 0.3)`,
        whiteSpace: "nowrap",
      }}
    >
      {badge.text}
    </span>
  );
}

function MetaBar({ meta }: { meta: MetaItem[] }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 24,
        padding: "12px 16px",
        background: "rgba(var(--accent-true-blue-rgb), 0.04)",
        border: "1px solid rgba(var(--accent-true-blue-rgb), 0.1)",
        borderRadius: 8,
        marginBottom: 18,
        flexWrap: "wrap",
      }}
    >
      {meta.map((m) => (
        <div
          key={m.label}
          style={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <span
            style={{
              fontSize: "var(--fs-micro)",
              color: "var(--text-muted)",
              letterSpacing: 1.5,
              textTransform: "uppercase",
            }}
          >
            {m.label}
          </span>
          <span
            style={{
              fontSize: "var(--fs-label)",
              color: m.valueAccent ? tone(m.valueAccent) : "var(--text-primary)",
              fontWeight: 600,
            }}
          >
            {m.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function KpiTile({ kpi }: { kpi: Kpi }) {
  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: "1px solid rgba(var(--accent-true-blue-rgb), 0.12)",
        borderRadius: 8,
        padding: "10px 12px",
      }}
    >
      <div
        style={{
          fontSize: "var(--fs-micro)",
          fontWeight: 600,
          letterSpacing: 1.5,
          textTransform: "uppercase",
          color: "var(--text-muted)",
          marginBottom: 4,
        }}
      >
        {kpi.label}
      </div>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: "var(--fs-title)",
          fontWeight: 700,
          lineHeight: 1,
          marginBottom: 2,
          color: tone(kpi.accent),
        }}
      >
        {kpi.value}
      </div>
      <div style={{ fontSize: "var(--fs-micro)", color: "var(--text-muted)" }}>{kpi.sub}</div>
    </div>
  );
}

function KpiRow({ kpis }: { kpis: Kpi[] }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
        gap: 8,
        marginBottom: 18,
      }}
    >
      {kpis.map((k) => (
        <KpiTile kpi={k} key={k.label} />
      ))}
    </div>
  );
}

function CalloutBlock({ callout, marginBottom }: { callout: Callout; marginBottom?: number }) {
  return (
    <div
      style={{
        padding: "12px 14px",
        borderRadius: 8,
        borderLeft: `3px solid ${tone(callout.accent)}`,
        background: `rgba(${toneRgb(callout.accent)}, 0.05)`,
        marginBottom: marginBottom ?? 10,
      }}
    >
      <div
        style={{
          fontSize: "var(--fs-label)",
          fontWeight: 700,
          letterSpacing: 1,
          marginBottom: 4,
          color: tone(callout.accent),
        }}
      >
        {callout.leadGlyph ? `${callout.leadGlyph} ` : ""}
        {callout.title}
      </div>
      <div
        style={{
          fontSize: "var(--fs-label)",
          color: "var(--text-secondary)",
          lineHeight: 1.6,
        }}
      >
        {callout.body}
      </div>
    </div>
  );
}

const TABLE_STYLE: CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "var(--fs-label)",
};

const TH_STYLE: CSSProperties = {
  fontSize: "var(--fs-micro)",
  fontWeight: 700,
  letterSpacing: 1.5,
  textTransform: "uppercase",
  color: "var(--text-muted)",
  padding: "6px 10px",
  textAlign: "left",
  borderBottom: "1px solid rgba(var(--accent-true-blue-rgb), 0.2)",
  background: "rgba(var(--accent-true-blue-rgb), 0.04)",
};

const TD_STYLE: CSSProperties = {
  padding: "8px 10px",
  color: "var(--text-secondary)",
  borderBottom: "1px solid rgba(var(--accent-true-blue-rgb), 0.07)",
  verticalAlign: "top",
  lineHeight: 1.5,
};

function OutputsTable({ rows }: { rows: OutputRow[] }) {
  return (
    <table style={TABLE_STYLE}>
      <thead>
        <tr>
          <th style={TH_STYLE}>Output</th>
          <th style={TH_STYLE}>Description</th>
          <th style={TH_STYLE}>Status</th>
          <th style={TH_STYLE}>Owner</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.output}>
            <td
              style={{
                ...TD_STYLE,
                fontWeight: 600,
                color: "var(--text-primary)",
              }}
            >
              {row.output}
            </td>
            <td style={TD_STYLE}>{row.description}</td>
            <td style={TD_STYLE}>
              <BadgePill badge={row.status} />
            </td>
            <td style={TD_STYLE}>{row.owner}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DecisionsList({ rows }: { rows: DecisionRow[] }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {rows.map((d) => (
        <CalloutBlock
          key={d.title}
          callout={{ title: d.title, body: d.body, accent: "true-blue" }}
          marginBottom={0}
        />
      ))}
    </div>
  );
}

function ReadinessBars({ rows }: { rows: ReadinessRow[] }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {rows.map((row) => (
        <div key={row.label}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 3,
            }}
          >
            <span style={{ fontSize: "var(--fs-label)", color: "var(--text-secondary)" }}>
              {row.label}
            </span>
            <span
              style={{
                fontSize: "var(--fs-label)",
                color: tone(row.accent),
                fontFamily: "var(--font-mono)",
              }}
            >
              {row.delta}
              {row.flag ? ` ${row.flag}` : ""}
            </span>
          </div>
          <div
            style={{
              height: 6,
              background: "rgba(var(--accent-true-blue-rgb), 0.08)",
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${row.pct}%`,
                height: "100%",
                background: tone(row.accent),
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function RiskTable({ rows }: { rows: RiskRow[] }) {
  return (
    <table style={TABLE_STYLE}>
      <thead>
        <tr>
          <th style={TH_STYLE}>ID</th>
          <th style={TH_STYLE}>Risk / Issue</th>
          <th style={TH_STYLE}>Category</th>
          <th style={TH_STYLE}>Severity</th>
          <th style={TH_STYLE}>Likelihood</th>
          <th style={TH_STYLE}>Owner</th>
          <th style={TH_STYLE}>Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id}>
            <td
              style={{
                ...TD_STYLE,
                fontFamily: "var(--font-mono)",
                color: "var(--accent-sky)",
              }}
            >
              {row.id}
            </td>
            <td style={TD_STYLE}>
              <strong style={{ color: "var(--text-primary)" }}>{row.title}</strong>{" "}
              {row.body}
            </td>
            <td style={TD_STYLE}>
              <BadgePill badge={row.category} />
            </td>
            <td
              style={{
                ...TD_STYLE,
                color: tone(row.severityAccent),
                fontWeight: 600,
              }}
            >
              {row.severity}
            </td>
            <td style={{ ...TD_STYLE, color: tone(row.likelihoodAccent) }}>
              {row.likelihood}
            </td>
            <td style={TD_STYLE}>{row.owner}</td>
            <td style={TD_STYLE}>
              <BadgePill badge={row.status} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ActionList({ rows }: { rows: ActionRow[] }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {rows.map((row) => (
        <div
          key={row.id}
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            padding: "9px 12px",
            background: "var(--bg-card)",
            border: "1px solid rgba(var(--accent-true-blue-rgb), 0.1)",
            borderRadius: 8,
          }}
        >
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-micro)",
              color: "var(--accent-sky)",
              width: 30,
              flexShrink: 0,
              paddingTop: 2,
            }}
          >
            {row.id}
          </span>
          <div
            style={{
              fontSize: "var(--fs-label)",
              color: "var(--text-secondary)",
              lineHeight: 1.55,
              flex: 1,
            }}
          >
            <strong style={{ color: "var(--text-primary)" }}>{row.title}</strong>{" "}
            {row.body}
          </div>
          <div
            style={{
              fontSize: "var(--fs-micro)",
              flexShrink: 0,
              textAlign: "right",
              minWidth: 110,
              color: tone(row.ownerAccent),
            }}
          >
            {row.owner}
            <br />
            <span style={{ fontSize: "var(--fs-micro)", color: "var(--text-muted)" }}>
              Due: {row.due}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

/* --------------------------- Empty ------------------------------- */

function EmptyReport({ message }: { message?: string } = {}) {
  return (
    <div
      style={{
        padding: "32px 12px",
        textAlign: "center",
        color: "var(--text-muted)",
        fontSize: "var(--fs-micro)",
        letterSpacing: 0.5,
      }}
    >
      <div style={{ fontSize: "1.75rem", marginBottom: 8 }}>{EM_DASH}</div>
      {message ?? "No session report registered for this workshop yet."}
    </div>
  );
}

/* ----------------------------- Panes ----------------------------- */

/**
 * Executive Summary pane — legacy `rpt-pane-executive` (lines 859-898):
 * meta bar + KPI cards + Purpose & Objective + Overall Assessment
 * callouts + Standout Contributions.
 */
function ExecutivePane({ data }: { data: ReportContent }) {
  return (
    <>
      <MetaBar meta={data.meta} />
      <KpiRow kpis={data.kpis} />

      <Section>
        <SectionHeading>Purpose &amp; Objective</SectionHeading>
        <Body>{data.executiveSummary.purpose.body}</Body>
      </Section>

      <Section>
        <SectionHeading>Overall Assessment</SectionHeading>
        {data.executiveSummary.callouts.map((c) => (
          <CalloutBlock callout={c} key={c.title} />
        ))}
      </Section>

      <Section>
        <SectionHeading>Standout Contributions</SectionHeading>
        <Body>{data.executiveSummary.contributions.body}</Body>
      </Section>
    </>
  );
}

/**
 * Outcomes pane — legacy `rpt-pane-outcomes` (lines 901-932):
 * Confirmed Outputs table + Key Decisions + Participant Readiness.
 */
function OutcomesPane({
  data,
  liveDecisions,
}: {
  data: ReportContent;
  liveDecisions: readonly Decision[];
}) {
  // CONV — live decisions are authoritative (same SSoT as the Log/Repository);
  // the curated WS4 sample is the fallback when none have been logged yet.
  const decisionRows: DecisionRow[] =
    liveDecisions.length > 0
      ? liveDecisions.map((d) => ({ title: d.title, body: d.rationale || "—" }))
      : data.outcomes.decisions;
  return (
    <>
      <Section>
        <SectionHeading>Confirmed Outputs</SectionHeading>
        <OutputsTable rows={data.outcomes.outputs} />
      </Section>

      <Divider />

      <Section>
        <SectionHeading>Key Decisions Made</SectionHeading>
        <DecisionsList rows={decisionRows} />
      </Section>

      <Section>
        <SectionHeading>Participant Readiness — Pre vs Post</SectionHeading>
        <ReadinessBars rows={data.outcomes.readiness} />
      </Section>
    </>
  );
}

/**
 * Risks & Issues pane — legacy `rpt-pane-risks` (lines 935-952):
 * the post-WS4 risk register table.
 */
function RisksPane({
  data,
  liveFindings,
}: {
  data: ReportContent;
  liveFindings: readonly Finding[];
}) {
  // CONV — the Findings register is the live "risks & issues" surface; when
  // findings exist they drive this table (mapped to the risk-row shape),
  // else the curated WS4 sample register shows.
  const riskRows: RiskRow[] =
    liveFindings.length > 0
      ? liveFindings.map((f, i) => ({
          id: `F-${String(i + 1).padStart(2, "0")}`,
          title: f.title,
          body: f.detail,
          category: { text: f.domain.toUpperCase(), accent: "true-blue" },
          severity: f.severity,
          severityAccent: severityToAccent(f.severity),
          likelihood: EM_DASH,
          likelihoodAccent: "muted",
          owner: f.owner || EM_DASH,
          status: { text: f.status.toUpperCase(), accent: findingStatusAccent(f.status) },
        }))
      : data.risks;
  return (
    <Section>
      <SectionHeading>Risk Register — Post WS4</SectionHeading>
      <RiskTable rows={riskRows} />
    </Section>
  );
}

/**
 * Next Steps pane — legacy `rpt-pane-next` (lines 953-992):
 * Immediate Actions + Backlog & Future Sprints + WS5 Scope Preview.
 */
function NextStepsPane({
  data,
  liveActions,
}: {
  data: ReportContent;
  liveActions: readonly ActionItem[];
}) {
  // CONV — the Actions register is the live "immediate actions" surface (same
  // SSoT as the Log/Repository); when actions exist they drive this list,
  // else the curated WS4 sample shows.
  const immediateRows: ActionRow[] =
    liveActions.length > 0
      ? liveActions.map((a, i) => ({
          id: `A-${String(i + 1).padStart(2, "0")}`,
          title: a.title,
          body: a.status ? `· ${a.status}` : "",
          owner: a.owner || EM_DASH,
          ownerAccent: "true-blue",
          due: a.due || EM_DASH,
        }))
      : data.nextSteps.immediate;
  return (
    <>
      <Section>
        <SectionHeading>Immediate Actions — Within 2 Weeks</SectionHeading>
        <ActionList rows={immediateRows} />
      </Section>

      <Divider />

      <Section>
        <SectionHeading>Backlog &amp; Future Sprints</SectionHeading>
        <ActionList rows={data.nextSteps.backlog} />
      </Section>

      <Divider />

      <Section>
        <SectionHeading>Workshop 5 — Scope Preview</SectionHeading>
        <CalloutBlock callout={data.nextSteps.nextWorkshopCallout} />
      </Section>
    </>
  );
}

/* --------------------------- Modal ------------------------------- */

export function ReportModal({ open, onOpenChange, workshopId }: ReportModalProps) {
  const [tab, setTab] = useState<TabId>("executive");
  // ADO #687 — a real, generated Report (from this workshop's actual
  // transcript/decisions/findings/actions) takes priority when the BFF has
  // produced one; the static WS4 fixture is now purely a fallback for WS4
  // when nothing has been generated yet, never a stand-in for a real
  // workshop's report.
  const generated = useGeneratedReport(workshopId);
  const authored = generated.data ?? getReport(workshopId);
  const wsId = workshopId as Workshop["id"];

  // Session date — every rendered date derives from the (override-aware)
  // agenda date instead of the frozen seed copies (UAT: live dates).
  const override = useEffectiveWorkshopOverride(workshopId);
  const sessionDate = resolveSessionDate(workshopId, override);
  const data = useMemo(() => {
    if (!authored || !sessionDate) return authored;
    return {
      ...authored,
      meta: authored.meta.map((m) => ({
        ...m,
        value: withSessionDate(m.value, sessionDate),
      })),
      footerSummary: withSessionDate(authored.footerSummary, sessionDate),
    };
  }, [authored, sessionDate]);

  // CONV — read the live registers so the report reflects the same SSoT as
  // the Log/Repository. Hydrated lazily on open (idempotent).
  const liveDecisions = useDecisions(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_DECISIONS as Decision[]),
  );
  const liveFindings = useFindings(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_FINDINGS as Finding[]),
  );
  const liveActions = useActions(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_ACTIONS as ActionItem[]),
  );
  useEffect(() => {
    if (!open) return;
    void useDecisions.getState().hydrate();
    void useFindings.getState().hydrate();
    void useActions.getState().hydrate();
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PanelDialogContent
        className="w-[86vw] max-w-[960px] border-0 bg-transparent p-0 shadow-none"
        style={{ outline: "none" }}
      >
        <DialogTitle className="sr-only">Session Report — Workshop {workshopId}</DialogTitle>
        <DialogDescription className="sr-only">
          Long-form session report covering executive summary, outcomes, risks, and
          next steps for {workshopId}.
        </DialogDescription>
        <div
          className="modal-shell"
          style={{ width: "100%", maxHeight: "85vh" }}
        >
          {/* HEADER — legacy `.modal-header` is `space-between` flex:
              title block on the LEFT, action cluster on the right. */}
          <div className="modal-header">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <FileText
                aria-hidden
                style={{ width: 16, height: 16, color: "var(--accent-gold)" }}
              />
              <span className="modal-title">REPORT</span>
              <span
                style={{
                  padding: "1px 8px",
                  background: "rgba(var(--accent-gold-rgb), 0.12)",
                  border: "1px solid rgba(var(--accent-gold-rgb), 0.35)",
                  borderRadius: 10,
                  fontSize: "var(--fs-micro)",
                  color: "var(--accent-gold)",
                  fontWeight: 600,
                  letterSpacing: 1,
                }}
              >
                ASSET LOCKER
              </span>
              <span
                style={{
                  padding: "1px 8px",
                  background: "rgba(var(--accent-sky-rgb), 0.08)",
                  border: "1px solid rgba(var(--accent-sky-rgb), 0.25)",
                  borderRadius: 10,
                  fontSize: "var(--fs-micro)",
                  color: "var(--accent-sky)",
                  fontWeight: 600,
                  letterSpacing: 1,
                }}
              >
                {workshopId} — HUMAN-AGENT WAYS OF WORKING
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                aria-hidden
                style={{
                  display: "inline-block",
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: "var(--accent-green)",
                  boxShadow: "var(--glow-green)",
                  animation: "pulse 2s infinite",
                }}
              />
              <span
                style={{
                  fontSize: "var(--fs-micro)",
                  color: "var(--text-muted)",
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  marginRight: 12,
                }}
              >
                Draft · In Review
              </span>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="modal-close-btn"
                aria-label="Close"
              >
                <X aria-hidden style={{ width: 12, height: 12 }} />
                CLOSE
              </button>
            </div>
          </div>

          {/* TAB BAR — legacy `.dt-tab-bar` (lines 809-814).
              Mirrors the four legacy report panes. */}
          <div className="dt-tab-bar" role="tablist" aria-label="Report sections">
            {TABS.map((t) => {
              const isActive = t.id === tab;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setTab(t.id)}
                  className={isActive ? "dt-tab dt-tab-active" : "dt-tab"}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* BODY — legacy `.modal-body` (overflow + scrollbar styling
              come from the class). `.dt-pane.dt-pane-active` provides
              the 16 px gutter; only one pane is rendered at a time, so
              it's always active. */}
          <div className="modal-body">
            <div className="dt-pane dt-pane-active">
              {generated.loading ? (
                <EmptyReport message="Loading generated report…" />
              ) : data === null ? (
                <EmptyReport
                  message={
                    generated.error
                      ? "Couldn't load the generated report — try again shortly."
                      : undefined
                  }
                />
              ) : tab === "executive" ? (
                <ExecutivePane data={data} />
              ) : tab === "outcomes" ? (
                <OutcomesPane data={data} liveDecisions={liveDecisions} />
              ) : tab === "risks" ? (
                <RisksPane data={data} liveFindings={liveFindings} />
              ) : (
                <NextStepsPane data={data} liveActions={liveActions} />
              )}
            </div>
          </div>

          {/* FOOTER — legacy `.modal-footer` chrome with right-aligned
              status badge ("DRAFT · 14 MAR 2026 · 14 ACTIONS · 6 RISKS"
              from `_legacy/ws4-assetlocker.js` line 988). */}
          <div className="modal-footer">
            <span className="modal-footer-label">XAES Phase Zero</span>
            <span className="modal-footer-sep">|</span>
            <span className="modal-footer-ws">Asset Locker — Report</span>
            <span className="modal-footer-sep">|</span>
            <span
              className="modal-footer-ws"
              style={{ color: "var(--accent-sky)" }}
            >
              {workshopId} — Human-Agent Ways of Working
            </span>
            {data && <span className="modal-footer-right">{data.footerSummary}</span>}
          </div>
        </div>
      </PanelDialogContent>
    </Dialog>
  );
}
